import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import React, { Attributes, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { isAfter } from 'date-fns';
import { SECONDS_TO_MILLISECONDS, Vertical, VERTICALS } from '@moda/portal-stanchions';
import { ProductDetailPageQuery, ProductDetailPageQueryVariables } from '../../generated/types';
import { REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS } from '../../constants';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import {
  useHasAccessToProductOrTrunkshow,
  HAS_ACCESS_TO_PRODUCT_FRAGMENT
} from '../../hooks/useHasAccessToProductOrTrunkshow';
import { hrefFor } from '../../routers';
import { Loading } from '../../components/Loading';
import { useCountry } from '../../components/CountrySelector';
import { NotFoundPage } from '../NotFoundPage';
import { ProductDetailPage, PRODUCT_DETAIL_PAGE_FRAGMENT } from './ProductDetailPage';

const PRODUCT_DETAIL_PAGE_QUERY: TypedDocumentNode<
  ProductDetailPageQuery,
  ProductDetailPageQueryVariables
> = gql`
  query ProductDetailPageQuery($variantId: String!, $countryCode: String) {
    variant(id: $variantId) {
      activeEndI: active_end_i
      gender
      designerSlug: designer_slug
      productSlug: slug
      ...HasAccessToProductFragment
      ...ProductDetailPageFragment
    }
  }
  ${HAS_ACCESS_TO_PRODUCT_FRAGMENT}
  ${PRODUCT_DETAIL_PAGE_FRAGMENT}
`;

interface Props extends Attributes {
  match: {
    params: { vertical: string; designerSlug: string; productSlug: string; variantId: string };
  };
}

export const ProductDetailPageContainer: React.FC<Props> = ({ match: { params } }) => {
  const { variantId } = params;
  const {
    country: { alpha2Code: countryCode }
  } = useCountry();

  const { data, loading, error, client } = useQuery(PRODUCT_DETAIL_PAGE_QUERY, {
    variables: { variantId, countryCode }
  });

  const { hasAccess, loading: loadingAccess } = useHasAccessToProductOrTrunkshow(data?.variant);

  // prefetch all variants to not show the loader when changing color
  useEffect(() => {
    if (data?.variant) {
      [data.variant.masterVariant, ...data.variant.otherVariants].forEach(variant => {
        if (variant && variant.id !== variantId) {
          client.query({
            query: PRODUCT_DETAIL_PAGE_QUERY,
            variables: { variantId: variant.id, countryCode }
          });
        }
      });
    }
  }, [client, countryCode, data, variantId]);

  if (loading || !data || loadingAccess) return <Loading />;

  if (error) throwQueryError(error);

  if (!data.variant) return <NotFoundPage />;

  const { variant } = data;

  const vertical = VERTICALS[variant.gender as Vertical].toLowerCase();
  const productSlug = variant.productSlug;
  const designerSlug = variant.designerSlug as string;

  // if the user does not have access to the product return a 404
  if (!hasAccess) return <NotFoundPage />;

  // if the product has expired a long time ago redirect to the designer page
  if (
    variant.activeEndI &&
    isAfter(
      new Date(),
      variant.activeEndI * SECONDS_TO_MILLISECONDS + REDIRECT_WHEN_TRUNKSHOW_EXPIRED_SINCE_MS
    )
  )
    return <Redirect to={hrefFor.DesignerPage({ vertical, designerSlug })} />;

  // if the url is constructed incorrectly, redirect to the correct url
  if (
    params.vertical !== vertical ||
    params.designerSlug !== designerSlug ||
    params.productSlug !== productSlug
  )
    return (
      <Redirect
        to={hrefFor.ProductDetailPage({ vertical, designerSlug, productSlug, variantId })}
      />
    );

  return <ProductDetailPage variant={variant} />;
};
